// ============================================================================
// ENGAGEMENT TYPES
// ============================================================================

export interface Engagement {
  id?: string;
  clientName: string;
  taxYearEnd: string; // ISO date string
  taxForm: string;
  description?: string;
  createdAt: number;
}

export interface Entity {
  id: string;
  engagementId: string;
  name: string;
}

export type TaxForm = '1120' | '1120-S' | '1065' | '1040-Sch-C';

// ============================================================================
// TRIAL BALANCE TYPES (Prepared for Phase 2)
// ============================================================================

/**
 * Trial Balance Account following 4-tier structure from System_Foundation.md
 * 
 * Financial values stored as integer cents (no floating point)
 * Math: Simple integer addition/subtraction only
 * Display: Zero values show as dash (-), negatives in red
 */
export interface TBAccount {
  id?: string;
  engagementId: string;
  accountNumber: string;
  accountName: string;
  entity: string; // Added entity to database schema

  // Input Values (integer cents)
  bookBalance: number;    // Changed from bookValue
  rje: number;            // Changed from rjeTotal
  aje: number;            // Changed from ajeTotal
  mAdj: number;           // Changed from mAdjTotal

  // Reference Numbers (optional, for tracking journal entries)
  jeNumber?: string;      // Journal Entry reference number
  mAdjNumber?: string;    // M-Adjustment reference number

  // Calculated Values (hydrated at runtime, NOT stored in database)
  // These are optional because they don't exist until Workspace.tsx hydrates them
  adjBook?: number;       // bookBalance + rje + aje
  ftax?: number;          // adjBook + mAdj
  relatedJEs?: { id: string; jeNumber: string }[];  // JE headers touching this account
  relatedMAdjs?: { id: string; jeNumber: string }[]; // M-Adj headers touching this account

  // Prior Year Values
  pyAdjBook: number;
  pyMAdj: number;
  pyFtax: number;
  pyBalance: number;

  // Metadata
  grouping: string;
  subGrouping?: string;
  mAdjRef?: string;           // M-Adjustment reference linked to this account
  createdAt: number;
  updatedAt: number;
}

/**
 * Journal Entry Line (for RJE, AJE, M-Adj)
 */
export interface JournalEntryLine {
  id?: string;
  engagementId: string;
  headerId: string;       // FK → JournalEntryHeader.id (Phase 4)
  accountId: string;
  type: 'RJE' | 'AJE' | 'M-ADJ';
  amount: number;         // integer cents
  description?: string;
  createdAt: number;
}

// ============================================================================
// JOURNAL ENTRY TYPES (Phase 4)
// ============================================================================

/**
 * One header per JE (e.g. AJE-1, RJE-3).
 * signoffState encodes the 3-tier progression as a single field:
 *   null → not started | 'Prepared' → tier 1 | 'Principal' → tiers 1+2 | 'Final' → all three
 */
export interface JournalEntryHeader {
  id?: string;
  engagementId: string;
  jeNumber: string;       // user label, e.g. "AJE-1"
  jeName: string;
  isRJE: boolean;         // false = AJE, true = RJE
  totalDebits: number;    // integer cents
  signoffState: 'Prepared' | 'Principal' | 'Final' | null;
  notes: string;
  cautionFlag?: boolean;              // deprecated — kept for DB migration compatibility
  updatedAt?: number;                 // unix ms; set on every JE save
  lastPrincipalReviewAt?: number | null; // unix ms; set when Principal tier is signed
  netAdjustmentCents?: number;        // M-Adj only — integer cents target; undefined = 0
  differenceType?: 'Permanent' | 'Temporary' | null; // M-Adj only — tax difference classification
  calcType?: 'Manual' | 'Standard' | 'Meals50' | 'BookTaxDiff' | 'BSChange' | 'Rollforward'
           | 'IncomeStatement' | 'BS_Liability' | 'BS_Liability_Payout' | 'BS_Asset' | 'BS_Asset_Payout';
  calcInputs?: Record<string, number>;
  entityId?: string;              // links header to an entity (set at creation, locked)
}

export type SignoffState = JournalEntryHeader['signoffState'];
export type JESortField = 'jeNumber' | 'jeName' | 'isRJE' | 'totalDebits' | 'signoffState';

// ============================================================================
// COLUMN CONFIGURATION TYPES (For Trial Balance Table)
// ============================================================================

/**
 * Column IDs for Trial Balance Table
 * Maps to fields in TBAccount interface
 */
export type ColumnId =
  | 'select'
  | 'accountNumber'
  | 'accountName'
  | 'bookBalance'
  | 'jeNumber'
  | 'rje'
  | 'aje'
  | 'adjBook'
  | 'mAdjNumber'
  | 'mAdj'
  | 'ftax'
  | 'grouping'
  | 'subGrouping'
  | 'mAdjRef'
  | 'pyAdjBook'
  | 'pyMAdj'
  | 'pyFtax';

/**
 * Column Configuration for Trial Balance Table
 * Defines width, visibility, and display properties for each column
 */
export interface ColumnConfig {
  id: ColumnId;
  label: string;
  width: number;
  minWidth: number;
  visible: boolean;
  isCurrency?: boolean;
  isReadOnly?: boolean;
}

// ============================================================================
// FILTER TYPES (Sprint 2.7)
// ============================================================================

/**
 * Match type for text-based filters
 */
export type FilterMatchType = 'contains' | 'equals' | 'startsWith' | 'notContains' | 'notEquals';

/**
 * Match type for numeric filters
 */
export type NumericMatchType = 'equals' | 'greaterThan' | 'lessThan' | 'greaterOrEqual' | 'lessOrEqual' | 'notEquals';

/**
 * Individual column filter configuration
 */
export interface ColumnFilter {
  value: string;
  matchType: FilterMatchType | NumericMatchType;
  isActive: boolean;
}

/**
 * Complete filter state for all filterable columns
 * Keys match the column identifiers in the trial balance table
 */
export interface FilterState {
  accountNumber: ColumnFilter;
  accountName: ColumnFilter;
  bookBalance: ColumnFilter;
  rje: ColumnFilter;
  aje: ColumnFilter;
  adjBook: ColumnFilter;
  mAdj: ColumnFilter;
  ftax: ColumnFilter;
  grouping: ColumnFilter;
  subGrouping: ColumnFilter;
  entity: ColumnFilter;
}

/**
 * Default empty filter for initialization
 */
export const DEFAULT_COLUMN_FILTER: ColumnFilter = {
  value: '',
  matchType: 'contains',
  isActive: false,
};

/**
 * Default filter state with all filters empty
 */
export const DEFAULT_FILTER_STATE: FilterState = {
  accountNumber: { ...DEFAULT_COLUMN_FILTER },
  accountName: { ...DEFAULT_COLUMN_FILTER },
  bookBalance: { ...DEFAULT_COLUMN_FILTER, matchType: 'equals' },
  rje: { ...DEFAULT_COLUMN_FILTER, matchType: 'equals' },
  aje: { ...DEFAULT_COLUMN_FILTER, matchType: 'equals' },
  adjBook: { ...DEFAULT_COLUMN_FILTER, matchType: 'equals' },
  mAdj: { ...DEFAULT_COLUMN_FILTER, matchType: 'equals' },
  ftax: { ...DEFAULT_COLUMN_FILTER, matchType: 'equals' },
  grouping: { ...DEFAULT_COLUMN_FILTER },
  subGrouping: { ...DEFAULT_COLUMN_FILTER },
  entity: { ...DEFAULT_COLUMN_FILTER },
};

// ============================================================================
// UTILITY TYPES
// ============================================================================

export type SortField = 'clientName' | 'taxYearEnd' | 'taxForm';
export type SortDirection = 'asc' | 'desc';

export interface UploadConflict {
  existing: Engagement;
  uploaded: Engagement;
}
